/* eslint-disable react/no-danger */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Dialog, DialogTitle, DialogContent, Divider, Tooltip, FormControlLabel, Switch, IconButton } from '@mui/material';
import { isEmpty } from 'lodash';
import Iconify from '../Iconify';
import { actionIconStyle } from '../../styles/CampaignPreviewModalStyle';
import { createTemplatePreviewDocument, resolveTemplateSamplePreview } from '../../utils/templateMergeTagPreview';

const TemplatePreviewPopup = ({
    campaignPreview,
    handleClosePreviewTemplate,
    previewData,
    handlePreviewSize,
    previewSize,
    enableSamplePreview = false,
}) => {
    const [useSamples, setUseSamples] = useState(false);
    const samplePreviewEnabled = enableSamplePreview && (!previewData?.channel || previewData.channel.toUpperCase() === 'EMAIL');
    const body = (previewData?.use_unlayer ? previewData?.unlayer_html : previewData?.body) || '';
    const subject = previewData?.subject || '';

    useEffect(() => {
        setUseSamples(false);
    }, [campaignPreview, previewData?.id, enableSamplePreview]);

    const showingSamples = samplePreviewEnabled && campaignPreview && useSamples;
    const sample = useMemo(() => (
        showingSamples ? resolveTemplateSamplePreview({
            subject, body, includeMustache: Boolean(previewData?.use_unlayer),
        }) : { subject, body, unavailableSampleKeys: [] }
    ), [showingSamples, subject, body, previewData?.use_unlayer]);
    const previewBody = showingSamples ? sample.body : body;
    const previewDocument = useMemo(() => {
        if (!samplePreviewEnabled) return previewBody;
        return campaignPreview ? createTemplatePreviewDocument(previewBody) : '';
    }, [samplePreviewEnabled, campaignPreview, previewBody]);

    const getSize = () => {
        if (previewSize === 'tablet') return '768px';
        if (previewSize === 'mobile') return '364px';
        return '100%';
    };

    const getSizeStyle = (device) => {
        if (device === previewSize) return { border: '1px solid red', borderRadius: '50%' };
        return {};
    };

    return (
        <>
            <Dialog
                fullWidth
                maxWidth={'lg'}
                open={campaignPreview}
                onClose={handleClosePreviewTemplate}
                sx={{ zIndex: 1300 }}
            >
                <DialogTitle sx={{ padding: '24px 16px 24px 24px' }}>
                    {!isEmpty(previewData) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ ...actionIconStyle, ...getSizeStyle('desktop') }}>
                                    <Tooltip title="Desktop">
                                        <IconButton aria-label="Desktop preview" onClick={() => handlePreviewSize('desktop')}>
                                        <Iconify
                                            sx={{ cursor: 'pointer', color: previewSize === 'desktop' ? 'red' : '#637381' }}
                                            icon={'material-symbols-light:desktop-mac-outline'}
                                            width={20}
                                            height={20}
                                        />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ ...actionIconStyle, ...getSizeStyle('tablet') }}>
                                    <Tooltip title="Tablet">
                                        <IconButton aria-label="Tablet preview" onClick={() => handlePreviewSize('tablet')}>
                                        <Iconify
                                            sx={{ cursor: 'pointer', color: previewSize === 'tablet' ? 'red' : '#33363F' }}
                                            icon={'lucide:tablet'}
                                            width={20}
                                            height={20}
                                        />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ ...actionIconStyle, ...getSizeStyle('mobile') }}>
                                    <Tooltip title="Mobile">
                                        <IconButton aria-label="Mobile preview" onClick={() => handlePreviewSize('mobile')}>
                                        <Iconify
                                            sx={{ cursor: 'pointer', color: previewSize === 'mobile' ? 'red' : '#33363F' }}
                                            icon={'heroicons-outline:device-mobile'}
                                            width={20}
                                            height={20}
                                        />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={actionIconStyle}>
                                    <IconButton aria-label="Close preview" onClick={handleClosePreviewTemplate}>
                                    <Iconify
                                        sx={{ cursor: 'pointer', color: '#33363F' }}
                                        icon={'ci:close-big'}
                                        width={20}
                                        height={20}
                                    />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogTitle>
                {!isEmpty(previewData) && (
                    <DialogContent sx={{ padding: '24px 0', borderTop: '1px solid rgba(145, 158, 171, 0.2)' }}>
                        {samplePreviewEnabled && (
                            <Box sx={{ px: 3, pb: 2 }}>
                                <FormControlLabel
                                    control={<Switch checked={useSamples} onChange={(event) => setUseSamples(event.target.checked)} />}
                                    label="Use fictional sample data"
                                />
                                <Typography variant="caption" display="block" color="text.secondary">
                                    {showingSamples
                                        ? 'Fictional preview only — sample values do not verify recipient data or change your template.'
                                        : 'Raw template — merge tags are shown as written.'}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                    External images and links are disabled in this preview.
                                </Typography>
                                {showingSamples && sample.unavailableSampleKeys.length > 0 && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                        No sample value for: {sample.unavailableSampleKeys.join(', ')}. These tags are left as written.
                                    </Typography>
                                )}
                            </Box>
                        )}
                        <Divider />
                        <Typography variant="h6" noWrap sx={{ fontSize: '18px !important', textAlign: 'center', p: '12px 24px' }}>
                            Re: {showingSamples ? sample.subject : subject}
                        </Typography>
                        <Divider />
                        {
                            <Box sx={{ p: '24px 24px', '.ql-container': { border: 'none', fontFamily: 'Public Sans,sans-serif' } }}>
                                <Box
                                    sx={{
                                        '.ql-editor img': { maxWidth: '550px !important' },
                                        '.ql-editor': { p: 0 },
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <iframe
                                        title="template_preview"
                                        id="serviceFrameSend"
                                        srcDoc={previewDocument}
                                        sandbox={samplePreviewEnabled ? '' : undefined}
                                        referrerPolicy={samplePreviewEnabled ? 'no-referrer' : undefined}
                                        width={getSize()}
                                        style={{ height: 'calc(100vh - 276px)', border: '1px solid #5e636e' }}
                                    />
                                </Box>
                            </Box>
                        }
                    </DialogContent>
                )}
            </Dialog>
        </>
    );
};

export default TemplatePreviewPopup;

TemplatePreviewPopup.propTypes = {
    campaignPreview: PropTypes.bool,
    handleClosePreviewTemplate: PropTypes.func,
    previewData: PropTypes.any,
    handlePreviewSize: PropTypes.func,
    previewSize: PropTypes.string,
    enableSamplePreview: PropTypes.bool,
};
